import { getSupabase } from '@/lib/supabase';

import { toApiError } from '../ApiError';

import { type ImportTaxSettings } from './types';

type Row = Record<string, unknown>;

const num = (v: unknown, fallback = 0): number =>
  v === null || v === undefined ? fallback : Number(v);
const nullableNum = (v: unknown): number | null =>
  v === null || v === undefined ? null : Number(v);

export function mapSettings(row: Row): ImportTaxSettings {
  const transport = Array.isArray(row.transport) ? (row.transport as Row[]) : [];
  return {
    customsRateUgxPerUsd: num(row.customs_rate_ugx_per_usd, 3750),
    importDutyPct: num(row.import_duty_pct),
    vatPct: num(row.vat_pct),
    withholdingTaxPct: num(row.withholding_tax_pct),
    infrastructureLevyPct: num(row.infrastructure_levy_pct),
    importDeclarationFeePct: num(row.import_declaration_fee_pct),
    envLevyMinAge: num(row.env_levy_min_age, 9),
    envLevyPassengerPct: num(row.env_levy_passenger_pct),
    envLevyGoodsPct: num(row.env_levy_goods_pct),
    maxAgePassenger: nullableNum(row.max_age_passenger),
    maxAgeGoodsLight: nullableNum(row.max_age_goods_light),
    maxAgeGoodsHeavy: nullableNum(row.max_age_goods_heavy),
    registrationFeeUgx: num(row.registration_fee_ugx),
    numberPlatesUgx: num(row.number_plates_ugx),
    personalisedPlatesUgx: num(row.personalised_plates_ugx),
    stampDutyUgx: num(row.stamp_duty_ugx),
    valuationCount: num(row.valuation_count),
    effectiveFrom: String(row.effective_from ?? ''),
    sourceNote: String(row.source_note ?? ''),
    transport: transport.map((t) => ({
      destination: String(t.destination),
      minKg: num(t.min_kg),
      maxKg: nullableNum(t.max_kg),
      amountUsd: num(t.amount_usd),
    })),
  };
}

/** Read-only figures for the calculators. Admin-editable in Kabex Hub. */
export async function getImportTaxSettings(): Promise<ImportTaxSettings> {
  try {
    const { data, error } = await getSupabase().rpc('get_import_tax_settings');
    if (error) throw error;
    if (!data) throw new Error('no settings row');
    return mapSettings(data as Row);
  } catch (error) {
    throw toApiError(error);
  }
}
